import React from "react"
import styles from "./visualiser-text-box.module.css"
import { useVisualiserTextBoxViewModel } from "./VisualiserTextBox.vm"

type VisualiserTextBoxProps = {
  font?: string
  fontWeight?: string
  fontSize?: number | string
  /** Auto-shrink floor in px; 8 when unset. */
  minFontSize?: number
  color?: string
  lineHeight?: number
  size?: {
    width: string
    minHeight: string
    maxHeight: string
    minWidth: string
    maxWidth: string
  }
  position?: {
    top?: string
    bottom?: string
    left?: string
    right?: string
  }
  maxLength?: number
  maxLines?: number
  maxCharsPerLine?: number
  initialValue?: string
  userEditable?: boolean
  value?: string
  style?: React.CSSProperties
  /** Overrides the browser-default placeholder colour; any opacity comes from the colour's alpha. */
  placeholderColor?: string
  /** Accessible name for the editable text box; the static text ignores it. */
  ariaLabel?: string
  setValue: (value: string) => void
}

export const VisualiserTextBox = ({
  font = "Figtree",
  fontWeight = "normal",
  fontSize = 21,
  minFontSize,
  lineHeight = 1.2,
  size,
  position,
  color,
  maxLength,
  maxLines,
  maxCharsPerLine,
  initialValue = "Tap to edit",
  userEditable = true,
  style,
  value,
  placeholderColor,
  ariaLabel,
  setValue,
}: VisualiserTextBoxProps) => {
  const vm = useVisualiserTextBoxViewModel({
    font,
    fontWeight,
    fontSize,
    minFontSize,
    lineHeight,
    size,
    position,
    maxLength,
    maxLines,
    maxCharsPerLine,
    initialValue,
    userEditable,
    value,
    color,
    style,
    placeholderColor,
    setValue,
  })

  if (!vm.userEditable) {
    return (
      <div
        ref={vm.staticRef}
        className={styles["visualiser-text-box"]}
        style={vm.textareaStyle as React.CSSProperties}
      >
        {vm.displayValue}
      </div>
    )
  }

  return (
    <textarea
      ref={vm.textareaRef}
      className={
        placeholderColor
          ? `${styles["visualiser-text-box"]} ${styles["visualiser-text-box--custom-placeholder"]}`
          : styles["visualiser-text-box"]
      }
      disabled={false}
      autoComplete="off"
      autoCorrect="off"
      spellCheck="false"
      style={vm.textareaStyle as React.CSSProperties}
      placeholder={vm.placeholder}
      aria-label={ariaLabel}
      value={vm.displayValue}
      onChange={vm.handleChange}
      rows={1}
    />
  )
}
